/**
 * ReportPreview — read-only magazine-style render of an authored report.
 *
 * Mirrors the layout in swa-axn-customer/src/pages/TechnicalReport.tsx so
 * what the analyst sees here is faithful to what the customer will see when
 * the report is sent. The two SVG charts (Feature/Heatmap) the customer's
 * version draws need fields the writing agent doesn't produce yet
 * (volume.trailingWeeks, funnel breakdown). Until they're wired, this preview
 * shows a slim "Charts will render in customer view" placeholder so analysts
 * still see the cover/spread/retro/news/colophon they need to vet.
 */

import { Pencil } from 'lucide-react';
import { format } from 'date-fns';
import { InlineMd, Md } from './markdown';
import type {
  AuthoredSections,
  AuthoredServiceDelivery,
  ReportCustomContent,
  ReportDetail,
  ReportStructuredData,
} from '../../data/types';

import '../../styles/magazine.css';

interface ReportPreviewProps {
  report: ReportDetail;
  /**
   * When provided, each top-level magazine section becomes click-targeted
   * and surfaces a small "Edit / Ask" overlay on hover. The handler receives
   * the section's stable key (e.g. 'spread', 'retro', 'colophon').
   * Omit to render in pure-preview mode (read-only, no overlays).
   */
  onSectionEdit?: (sectionKey: string) => void;
  /**
   * When provided, the service-delivery section surfaces an "Edit other work"
   * overlay (and a slim add-prompt when the section is absent) so the analyst
   * can author/edit the free-form write-up during vetting. Separate from
   * onSectionEdit because it edits authoredSections.serviceDelivery.otherWork
   * in place rather than text-replacing the section.
   */
  onEditOtherWork?: () => void;
}

export function ReportPreview({ report, onSectionEdit, onEditOtherWork }: ReportPreviewProps) {
  const sections = report.aiGeneratedContent;
  if (!sections) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
        This report has no authored content yet — re-run generate to produce it.
      </div>
    );
  }

  const monthLabel = format(new Date(report.periodStart), 'MMMM yyyy').toUpperCase();
  const issueLabel = `ISSUE ${sections.issueNumber} · ${monthLabel}`;
  const orgName = report.tenantDisplayName ?? 'the customer';

  // Build a sectionName -> 'replace' override lookup so any section the
  // analyst has overridden renders as an analyst block instead of the AI prose.
  const overrides = new Map<string, ReportCustomContent>();
  for (const c of report.customContent) {
    if (c.position === 'replace') overrides.set(c.sectionName, c);
  }

  return (
    <div className="mag">
      <div className="mag-issue">
        <EditableSection sectionKey="cover" override={overrides.get('cover')} onEdit={onSectionEdit}>
          <Cover report={report} sections={sections} orgName={orgName} />
        </EditableSection>
        <EditableSection sectionKey="spread" override={overrides.get('spread')} onEdit={onSectionEdit}>
          <Spread sections={sections} issueLabel={issueLabel} />
        </EditableSection>
        <EditableSection sectionKey="featureIntro" override={overrides.get('featureIntro')} onEdit={onSectionEdit}>
          <Feature sections={sections} structured={report.structuredData} />
        </EditableSection>
        <EditableSection sectionKey="mitre" override={overrides.get('mitre')} onEdit={onSectionEdit}>
          <Heatmap sections={sections} structured={report.structuredData} />
        </EditableSection>
        <EditableSection sectionKey="retro" override={overrides.get('retro')} onEdit={onSectionEdit}>
          <Retro sections={sections} customContent={report.customContent} />
        </EditableSection>
        <EditableSection sectionKey="industry" override={overrides.get('industry')} onEdit={onSectionEdit}>
          <News sections={sections} />
        </EditableSection>
        {sections.serviceDelivery ? (
          <div
            className={onEditOtherWork ? 'group/section relative' : undefined}
            data-section="serviceDelivery"
          >
            <ServiceDeliverySection sd={sections.serviceDelivery} />
            {onEditOtherWork && (
              <button
                type="button"
                onClick={onEditOtherWork}
                className="absolute top-2 right-2 z-10 opacity-0 group-hover/section:opacity-100 transition-opacity bg-bg-secondary/95 backdrop-blur border border-border-default text-text-primary text-[11px] px-2 py-1 rounded-md shadow-sm hover:border-brand-blue flex items-center gap-1.5"
                title="Edit the free-form 'other work' write-up"
              >
                <Pencil className="w-3 h-3" />
                Edit other work
              </button>
            )}
          </div>
        ) : onEditOtherWork ? (
          <button
            type="button"
            onClick={onEditOtherWork}
            className="w-full my-4 px-4 py-3 rounded-lg border border-dashed border-border-default text-xs text-text-secondary hover:border-brand-blue hover:text-text-primary transition-colors flex items-center justify-center gap-1.5"
          >
            <Pencil className="w-3 h-3" />
            Add an “Other work this month” write-up (service delivery)
          </button>
        ) : null}
        <EditableSection sectionKey="colophon" override={overrides.get('colophon')} onEdit={onSectionEdit}>
          <Colophon sections={sections} orgName={orgName} />
        </EditableSection>
      </div>
    </div>
  );
}

/**
 * Wraps a magazine section. Three responsibilities:
 *   1. If an analyst override exists for this sectionKey, render it instead
 *      of the AI children (the override is rendered as a CustomBlock so the
 *      visual contrast tells the analyst "you overrode this").
 *   2. When `onEdit` is wired, surface a small floating "Edit / Ask" pill
 *      on hover that opens the section drawer.
 *   3. Pure preview when `onEdit` is not provided — used by read-only views.
 */
function EditableSection({
  sectionKey,
  override,
  onEdit,
  children,
}: {
  sectionKey: string;
  override?: ReportCustomContent;
  onEdit?: (sectionKey: string) => void;
  children: React.ReactNode;
}) {
  const editable = !!onEdit;
  return (
    <div
      className={editable ? 'group/section relative' : undefined}
      data-section={sectionKey}
    >
      {override ? <CustomBlock block={override} /> : children}
      {editable && (
        <button
          type="button"
          onClick={() => onEdit?.(sectionKey)}
          className="absolute top-2 right-2 z-10 opacity-0 group-hover/section:opacity-100 transition-opacity bg-bg-secondary/95 backdrop-blur border border-border-default text-text-primary text-[11px] px-2 py-1 rounded-md shadow-sm hover:border-brand-blue flex items-center gap-1.5"
          title={`Edit or ask about the ${sectionKey} section`}
        >
          <Pencil className="w-3 h-3" />
          Edit / Ask
        </button>
      )}
    </div>
  );
}

// ============================================================================
// Sections
// ============================================================================

/**
 * Magazine cover — mirrors the customer's issue cover (Reports.tsx) minus the
 * router Link / motion wrapper, so the analyst preview (and the PDF rendered
 * from it) leads with the same front page the customer sees.
 */
function Cover({
  report,
  sections,
  orgName,
}: {
  report: ReportDetail;
  sections: AuthoredSections;
  orgName: string;
}) {
  const periodLabel = `${format(new Date(report.periodStart), 'd MMM')} → ${format(new Date(report.periodEnd), 'd MMM yyyy')}`;
  const monthLabel = format(new Date(report.periodStart), 'MMMM yyyy');
  const issueNumber = sections.issueNumber;

  const kicker = sections.cover.kicker ?? `Monthly report · Issue No. ${issueNumber ?? '—'} · ${monthLabel}`;
  const headline = sections.cover.headline ?? report.reportTitle;
  const dek = sections.cover.dek ?? `A full security analysis for ${orgName} covering ${monthLabel}.`;
  const atAGlance = sections.cover.atAGlance ?? [];

  return (
    <div className="mag-cover">
      <div className="mag-cover-l">
        <div className="mag-cover-top">
          <div className="mag-cover-kick">
            <span className="bar" />
            {kicker}
          </div>
          <h2 className="mag-cover-headline"><InlineMd>{headline}</InlineMd></h2>
          <p className="mag-cover-dek"><InlineMd>{dek}</InlineMd></p>
        </div>
        <div className="mag-cover-bottom">
          <div className="mag-cover-meta">
            <span className="lbl">Prepared for</span>
            <strong>{orgName}</strong> · IT Operations
          </div>
          <div className="mag-cover-meta">
            <span className="lbl">Period</span>
            <strong>{periodLabel}</strong>
          </div>
        </div>
      </div>
      <div className="mag-cover-r">
        <div className="hd">At a glance · {monthLabel}</div>
        {atAGlance.map((item, i) => (
          <div className="mag-cv-stat" key={i}>
            <div className="k">{item.k}</div>
            <div className="n">{fmtN(item.n)}</div>
            {item.d && <div className="d">{item.d}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function Spread({
  sections,
  issueLabel,
}: {
  sections: AuthoredSections;
  issueLabel: string;
}) {
  const s = sections.spread;
  return (
    <div className="mag-spread">
      <div className="mag-sp-l">
        <div className="masthead">
          <span>ALEXEIN · MONTHLY REPORT</span>
          <span className="dt">{issueLabel}</span>
        </div>
        <div className="eyebrow">{s.eyebrow}</div>
        <h2 className="big"><InlineMd>{s.bigHeadline}</InlineMd></h2>
        <p className="lede"><InlineMd>{s.lede}</InlineMd></p>
        <div className="body">
          {s.body.map((p, i) => (
            <p key={i}><InlineMd>{p}</InlineMd></p>
          ))}
        </div>
        <blockquote className="pull">
          {s.pullQuote.text}
          <cite>— {s.pullQuote.cite}</cite>
        </blockquote>
      </div>
      <div className="mag-sp-r">
        {sections.statCards.map((card, i) => (
          <div key={i} className={`mag-card-stat ${i === 0 ? 'hero' : ''}`}>
            <div className="k">{card.k}</div>
            <div className="n">
              {fmtN(card.n)}
              {card.unit && <span className="u">{card.unit}</span>}
            </div>
            <div className="d"><InlineMd>{card.narrative}</InlineMd></div>
            {card.footItems && card.footItems.length > 0 && (
              <div className="foot-no">
                {card.footItems.map((item, j) => (
                  <div className="item" key={j}>
                    <div className="nn">{fmtN(item.n)}</div>
                    <div className="kk">{item.k}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function Feature({
  sections,
  structured,
}: {
  sections: AuthoredSections;
  structured: ReportStructuredData;
}) {
  return (
    <div className="mag-feature">
      <div className="mag-section-h">
        <div>
          <div className="k">The month in charts</div>
          <h2>Three pictures that explain the month.</h2>
        </div>
        <div className="intro"><InlineMd>{sections.featureIntro}</InlineMd></div>
      </div>

      <div className="mag-chart-grid">
        <div className="mag-ch">
          <div className="mag-ch-head">
            <div>
              <div className="mag-ch-t">Incident volume &amp; MTTR · trailing weeks</div>
              <div className="mag-ch-s"><InlineMd>{sections.chartCaptions.volumeMttr}</InlineMd></div>
            </div>
            <div className="mag-ch-leg">
              <div className="li">
                <span className="sw" style={{ background: 'var(--ax-navy)' }} />
                Incidents
              </div>
              <div className="li line">
                <span className="sw" style={{ background: 'var(--ax-lime)', height: 2 }} />
                MTTR (min)
              </div>
            </div>
          </div>
          <VolumeMttrChart weeks={structured.volume?.trailingWeeks ?? []} />
        </div>

        <div className="mag-ch">
          <div className="mag-ch-head">
            <div>
              <div className="mag-ch-t">Signal → resolution funnel</div>
              <div className="mag-ch-s"><InlineMd>{sections.chartCaptions.funnel}</InlineMd></div>
            </div>
          </div>
          <Funnel rows={structured.funnel ?? []} />
        </div>
      </div>
    </div>
  );
}

type MitreRowAny = ReportStructuredData['mitre'][number] & {
  id?: string;
  cells?: number[];
};

function mergeMitreRows(rows: MitreRowAny[]) {
  type Bucket = { tactic: string; label: string; count: number; cells: number[] };
  const buckets = new Map<string, Bucket>();
  for (const row of rows) {
    const raw = (row.id ?? row.technique ?? '').trim();
    if (!raw) continue;
    const idMatch = raw.match(/^T\d+(?:\.\d+)?/i);
    const techniqueId = idMatch ? idMatch[0].toUpperCase() : raw;
    const friendly = idMatch ? raw.slice(idMatch[0].length).replace(/^\s*[-–—]\s*/, '').trim() : '';
    const tactic = (row.tactic ?? '').trim();
    const namedTactic = tactic && tactic.toLowerCase() !== 'unknown' ? tactic : '';
    const count = row.count ?? 0;
    const existing = buckets.get(techniqueId);
    if (existing) {
      existing.count = Math.max(existing.count, count);
      if (!existing.tactic && namedTactic) existing.tactic = namedTactic;
      if (friendly && existing.label === techniqueId) existing.label = `${techniqueId} — ${friendly}`;
    } else {
      buckets.set(techniqueId, {
        tactic: namedTactic,
        label: friendly ? `${techniqueId} — ${friendly}` : techniqueId,
        count,
        cells: row.cells ?? [],
      });
    }
  }
  return Array.from(buckets.values())
    .sort((a, b) => b.count - a.count)
    .map((b) => ({ tactic: b.tactic || 'Unknown', id: b.label, count: b.count, cells: b.cells }));
}

function Heatmap({
  sections,
  structured,
}: {
  sections: AuthoredSections;
  structured: ReportStructuredData;
}) {
  const rows = mergeMitreRows(structured.mitre ?? []).slice(0, 10);
  return (
    <div className="mag-heatmap">
      <div className="mag-section-h">
        <div>
          <div className="k">Adversary behaviour · MITRE ATT&amp;CK</div>
          <h2>Where the adversary put their effort this month.</h2>
        </div>
        <div className="intro"><InlineMd>{sections.chartCaptions.heatmap}</InlineMd></div>
      </div>
      <div className="mag-hm-grid">
        {rows.length === 0 ? (
          <div className="text-sm text-slate-500 py-6 text-center">
            No MITRE activity recorded for this period.
          </div>
        ) : (
          rows.map((row, i) => {
            const cellRow = row.cells && row.cells.length > 0
              ? row.cells
              : Array.from({ length: 10 }, (_, j) => {
                  const intensity = Math.min(4, Math.max(0, Math.round((row.count / Math.max(1, rows[0]?.count ?? 1)) * 4)));
                  return j < intensity * 2 ? Math.min(4, intensity) : 0;
                });
            return (
              <div className="mag-hm-row" key={i}>
                <div className="mag-hm-tac">
                  {row.tactic}
                  {row.id && <span className="id">{row.id}</span>}
                </div>
                <div className="mag-hm-cells">
                  {cellRow.map((v, j) => (
                    <div className="mag-hm-cell" data-v={v} key={j} />
                  ))}
                </div>
                <div className="mag-hm-count">{row.count}</div>
              </div>
            );
          })
        )}
        <div className="mag-hm-legend">
          <span>Less</span>
          <div className="swatches">
            <span className="sw" style={{ background: 'var(--n-ul)' }} />
            <span className="sw" style={{ background: '#E8EDFA' }} />
            <span className="sw" style={{ background: '#C1CDEC' }} />
            <span className="sw" style={{ background: '#1D3577' }} />
            <span className="sw" style={{ background: 'var(--ax-navy)' }} />
          </div>
          <span>More</span>
        </div>
      </div>
    </div>
  );
}

function VolumeMttrChart({ weeks }: { weeks: Array<{ count?: number; mttrMin?: number | null; label?: string }> }) {
  const safeWeeks = weeks.length > 0 ? weeks : Array.from({ length: 8 }, () => ({ count: 0, mttrMin: 0 }));
  const maxCount = Math.max(40, ...safeWeeks.map((w) => w.count ?? 0));
  const maxMttr = Math.max(30, ...safeWeeks.map((w) => w.mttrMin ?? 0));

  const chartLeft = 40;
  const chartRight = 680;
  const chartTop = 40;
  const chartBottom = 200;
  const chartWidth = chartRight - chartLeft;
  const chartHeight = chartBottom - chartTop;
  const barWidth = Math.max(6, Math.min(40, (chartWidth - 24) / safeWeeks.length - 6));
  const step = safeWeeks.length > 0 ? chartWidth / safeWeeks.length : 0;

  const gridLines = [chartTop, chartTop + chartHeight / 4, chartTop + chartHeight / 2, chartTop + (3 * chartHeight) / 4];
  const points = safeWeeks
    .map((w, i) => {
      const x = chartLeft + step * i + step / 2;
      const mttr = w.mttrMin ?? 0;
      const y = chartBottom - (mttr / maxMttr) * chartHeight;
      return `${x.toFixed(1)},${y.toFixed(1)}`;
    })
    .join(' ');

  return (
    <svg viewBox="0 0 700 240" className="mag-chart-svg">
      <g stroke="#E8E8E8" strokeWidth={1}>
        {gridLines.map((y, i) => (
          <line key={i} x1={chartLeft} y1={y} x2={chartRight} y2={y} />
        ))}
      </g>
      <g fontFamily="JetBrains Mono" fontSize={9.5} fill="#A6A6A6">
        {[maxCount, Math.round(maxCount * 0.75), Math.round(maxCount * 0.5), Math.round(maxCount * 0.25)].map((v, i) => (
          <text key={i} x={chartLeft - 6} y={gridLines[i] + 3} textAnchor="end">{v}</text>
        ))}
      </g>
      <g fill="#09193B">
        {safeWeeks.map((w, i) => {
          const count = w.count ?? 0;
          const h = (count / maxCount) * chartHeight;
          const x = chartLeft + step * i + (step - barWidth) / 2;
          const y = chartBottom - h;
          const isRecent = i >= safeWeeks.length - 4;
          return (
            <rect key={i} x={x} y={y} width={barWidth} height={Math.max(2, h)} fill={isRecent ? '#1D3577' : '#09193B'} />
          );
        })}
      </g>
      {safeWeeks.some((w) => (w.mttrMin ?? 0) > 0) && (
        <polyline fill="none" stroke="#B9FF4D" strokeWidth={2.5} strokeLinejoin="round" strokeLinecap="round" points={points} />
      )}
    </svg>
  );
}

function Funnel({ rows }: { rows: NonNullable<ReportStructuredData['funnel']> }) {
  if (!rows || rows.length === 0) {
    return (
      <div className="py-6 text-center text-sm text-slate-500">
        Funnel data not yet available for this issue.
      </div>
    );
  }
  const max = Math.max(...rows.map((r) => r.n));
  return (
    <div className="mag-funnel">
      {rows.map((r, i) => {
        const width = max > 0 ? Math.max(12, (r.n / max) * 100) : 100;
        return (
          <div className="mag-fn-row" key={i}>
            <div className="k">{r.k}</div>
            <div className={`mag-fn-bar ${r.bar}`} style={{ width: `${width}%` }}>
              {r.n.toLocaleString()}
            </div>
            <div className="n">
              {r.n}
              <span className="pc">{r.pct.toFixed(r.pct < 1 ? 3 : 1)}%</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}

function Retro({
  sections,
  customContent,
}: {
  sections: AuthoredSections;
  customContent: ReportCustomContent[];
}) {
  const customForRetro = customContent.filter((c) => c.sectionName === 'retro');
  const prepend = customForRetro.filter((c) => c.position === 'prepend');
  const append = customForRetro.filter((c) => c.position === 'append');
  const replace = customForRetro.find((c) => c.position === 'replace');

  return (
    <div className="mag-retro">
      <div className="mag-section-h">
        <div>
          <div className="k">Incident retrospective</div>
          <h2>What we responded to, and what changed.</h2>
        </div>
        <div className="intro">
          A closer read on the incidents we handled. What happened, what Alexein
          did, what we learned.
        </div>
      </div>

      {prepend.map((c) => (
        <CustomBlock key={c.contentId} block={c} />
      ))}

      {replace ? (
        <CustomBlock block={replace} />
      ) : sections.retroCards.length === 0 ? (
        <div className="text-sm text-slate-500 py-4 text-center">
          No escalated incidents for this period.
        </div>
      ) : (
        sections.retroCards.map((card, i) => (
          <div className="mag-retro-card" key={i}>
            <div className="mag-retro-idx">
              {String(i + 1).padStart(2, '0')}
              <span className="sm">{card.title.split('—')[0]?.trim() || 'Incident'}</span>
            </div>
            <div>
              <div className="mag-retro-title">{card.title}</div>
              <div className="mag-retro-tags">
                {card.tags.map((t, j) => (
                  <span key={j}>{t}</span>
                ))}
              </div>
              <div className="mag-retro-narrative" style={{ marginTop: 14 }}>
                {card.narrative.map((p, j) => (
                  <p key={j}><InlineMd>{p}</InlineMd></p>
                ))}
              </div>
              {card.metrics && Object.keys(card.metrics).length > 0 && (
                <div className="mag-retro-metrics">
                  {Object.entries(card.metrics).map(([k, n], j) => (
                    <div className="m" key={j}>
                      <div className="n">{n}</div>
                      <div className="k">{k}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mag-retro-lessons">
              <h4>What changed</h4>
              <ul>
                {card.lessons.map((lesson, j) => (
                  <li key={j}>
                    <span className="dot" />
                    <InlineMd>{lesson}</InlineMd>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        ))
      )}

      {append.map((c) => (
        <CustomBlock key={c.contentId} block={c} />
      ))}
    </div>
  );
}

function News({ sections }: { sections: AuthoredSections }) {
  if (!sections.industryCards || sections.industryCards.length === 0) return null;
  const count = sections.industryCards.length;
  const countWord = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'][count] ?? String(count);
  const headline = count === 1
    ? `${countWord} story for you this month.`
    : `${countWord} stories for you this month.`;
  const counter = `${String(count).padStart(2, '0')} / ${String(count).padStart(2, '0')} Relevant`;
  return (
    <div className="mag-news">
      <header className="mag-news-head">
        <div className="kicker"><span className="dot" />The wider world</div>
        <h2>{headline}</h2>
        <div className="counter">{counter}</div>
      </header>
      <div className="mag-news-feed">
        {sections.industryCards.map((card, i) => (
          <article className="mag-news-item" key={i}>
            {card.image
              ? <img src={card.image} alt={card.title} className="mag-news-item-img" />
              : <div className="mag-news-item-img-placeholder" />}
            <div className="mag-news-item-text">
              <span className="mag-news-item-cat">{card.cat}</span>
              <h3 className="mag-news-item-title">{card.title}</h3>
              <Md className="mag-news-item-body">{card.body}</Md>
              {card.applied && (
                <div className="mag-news-item-applied"><InlineMd>{card.applied}</InlineMd></div>
              )}
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

function ServiceDeliverySection({ sd }: { sd: AuthoredServiceDelivery }) {
  const cat = (label: string, items: string[]) =>
    items.length > 0 ? (
      <div className="mag-sd-cat">
        <div className="mag-sd-label">{label}</div>
        <ul className="mag-sd-list">
          {items.map((it, i) => (
            <li key={i}><InlineMd>{it}</InlineMd></li>
          ))}
        </ul>
      </div>
    ) : null;

  const h = sd.hunts;
  const hasHunts = h.count != null || !!h.summary || h.resultingAnalytics.length > 0;

  return (
    <div className="mag-service">
      <div className="mag-section-h">
        <div>
          <div className="k">Service delivery</div>
          <h2>What we did for you this month.</h2>
        </div>
        <div className="intro">
          Platform engineering, detections, and threat hunting delivered on your behalf.
        </div>
      </div>
      <div className="mag-sd-grid">
        {cat('Solutions updated', sd.solutionsUpdated)}
        {cat('Solutions installed', sd.solutionsInstalled)}
        {cat('Analytics updated', sd.analyticsUpdated)}
        {cat('Analytics enabled', sd.analyticsEnabled)}
        {hasHunts && (
          <div className="mag-sd-cat">
            <div className="mag-sd-label">
              Hunts completed{h.count != null ? ` — ${h.count}` : ''}
            </div>
            {h.summary && <p className="mag-sd-p"><InlineMd>{h.summary}</InlineMd></p>}
            {h.resultingAnalytics.length > 0 && (
              <ul className="mag-sd-list">
                {h.resultingAnalytics.map((a, i) => (
                  <li key={i}><InlineMd>{a}</InlineMd></li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
      {sd.cloudAppDiscovery.length > 0 && (
        <div className="mag-sd-cat" style={{ marginTop: 16 }}>
          <div className="mag-sd-label">Cloud app discovery — for review</div>
          {sd.cloudAppDiscovery.map((a, i) => (
            <div className="mag-sd-app" key={i}>
              <b>{a.name}</b>
              <div className="mag-sd-app-meta">
                {a.hosting && <span>Hosting: {a.hosting}</span>}
                {a.dataCenter && <span>Data center: {a.dataCenter}</span>}
                {a.auth && <span>Auth: {a.auth}</span>}
                {a.complianceScore && <span>Compliance: {a.complianceScore}</span>}
                {a.usage && <span>Usage: {a.usage}</span>}
                {a.traffic && <span>Traffic: {a.traffic}</span>}
              </div>
            </div>
          ))}
        </div>
      )}
      {sd.otherWork && (
        <div className="mag-sd-cat" style={{ marginTop: 16 }}>
          <div className="mag-sd-label">Other work this month</div>
          <Md className="mag-sd-md">{sd.otherWork}</Md>
        </div>
      )}
    </div>
  );
}

function Colophon({
  sections,
  orgName,
}: {
  sections: AuthoredSections;
  orgName: string;
}) {
  return (
    <div className="mag-colophon">
      <div className="mag-colo-l">
        <span className="lg">About this report</span>
        <span><InlineMd>{sections.colophon.bodyText}</InlineMd></span>
      </div>
      <div className="mag-signoff">
        <img className="mag-logo-signature" src="/alexein-logo.svg" alt="Alexein" />
        <div className="mag-whom">
          <b>{sections.colophon.signedBy || 'The Alexein Managed SOC'}</b>
          <div className="role">Authored on behalf of {orgName}</div>
        </div>
      </div>
    </div>
  );
}

function CustomBlock({ block }: { block: ReportCustomContent }) {
  return (
    <div
      style={{
        marginBottom: 22,
        padding: '20px 22px',
        background: 'var(--lime-ul)',
        border: '1px solid var(--ax-lime)',
        borderRadius: 8,
      }}
    >
      <div
        style={{
          fontFamily: 'var(--font-head)',
          fontSize: 10,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          color: 'var(--mute)',
          fontWeight: 700,
          marginBottom: 6,
        }}
      >
        Analyst note · {block.position}
        {block.customSectionTitle ? ` · ${block.customSectionTitle}` : ''}
      </div>
      <div
        style={{
          fontFamily: 'var(--font-body)',
          fontSize: 13,
          lineHeight: 1.6,
          color: 'var(--ink)',
          whiteSpace: 'pre-wrap',
        }}
        dangerouslySetInnerHTML={{ __html: renderInline(block.customText) }}
      />
    </div>
  );
}

// ============================================================================
// Helpers
// ============================================================================
//
// Authored report copy renders through <Md>/<InlineMd> (see ./markdown). The
// regex pair below survives only for CustomBlock, whose analyst-override text
// comes from a plain textarea and is rendered `pre-wrap` — running it through
// markdown would swallow the single newlines the analyst typed.

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

// Thousands separators on numeric values; strings pass through unchanged.
function fmtN(v: string | number): string {
  return typeof v === 'number' ? v.toLocaleString('en-US') : v;
}

function renderInline(text: string): string {
  let out = escapeHtml(text);
  out = out.replace(/\*\*(.+?)\*\*/g, '<b>$1</b>');
  out = out.replace(/\*(.+?)\*/g, '<i>$1</i>');
  return out;
}
